import React, { createContext, useContext } from 'react';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';
import { useFilterState } from './StateProvider';

const PredicateContext = createContext<({ legs }) => boolean>(() => false);

const getId = ({ id }) => id;

const getCheckedAirportIds = (airports) =>
  new Set((airports || []).filter(({ checked }) => checked).map(getId));

const alwaysTrue = () => true;

const createSegmentPredicate = ({ departureAirports, destinationAirports }) => {
  const selectedDepartureIds = getCheckedAirportIds(departureAirports);
  const selectedDestinationIds = getCheckedAirportIds(destinationAirports);

  const hasDepartureAirports = departureAirports
    ? ({ id }) => selectedDepartureIds.has(id)
    : alwaysTrue;
  const hasDestinationAirports = destinationAirports
    ? ({ id }) => selectedDestinationIds.has(id)
    : alwaysTrue;

  return ({ sections }) => {
    const { departure } = sections[0];
    const { destination } = sections[sections.length - 1];

    return (
      hasDepartureAirports(departure) && hasDestinationAirports(destination)
    );
  };
};

const createRoundTripSegmentPredicates = (filterRequest) => {
  const { departureAirports, destinationAirports } = filterRequest;
  return [
    createSegmentPredicate(filterRequest),
    createSegmentPredicate({
      departureAirports: destinationAirports,
      destinationAirports: departureAirports,
    }),
  ];
};

const createItineraryPredicate = (filterRequest, tripType) => {
  if (!filterRequest.airports) {
    return alwaysTrue;
  }
  const { airports } = filterRequest;
  const segmentPredicates =
    tripType === 'ROUND_TRIP'
      ? createRoundTripSegmentPredicates(airports[0])
      : airports.map(createSegmentPredicate);
  return ({ legs }) => {
    return legs.every((leg, index) =>
      segmentPredicates[index](leg.segments[0])
    );
  };
};

export const usePredicate = () => useContext(PredicateContext);

export default function PredicateProvider({ children }) {
  const airportState = useFilterState();
  const { tripType } = usePerformedSearchRequest() || {};
  const airportsPredicate = createItineraryPredicate(airportState, tripType);
  return (
    <PredicateContext.Provider value={airportsPredicate}>
      {children}
    </PredicateContext.Provider>
  );
}
